import {LAYOUT_POLICY} from "../data/layoutPolicy";

export type LayoutOption = {
  /** Canonical layout id — written to the URL and consumed by scene resolution. */
  id: number;
  /** SVG filename served from /Layouts/<channel>/<iconId>.svg. */
  iconId: string;
};

/**
 * Entry shape accepted by a LAYOUT_POLICY `layouts` array. Either a numeric
 * shorthand (id doubles as iconId) or an explicit object with overrides.
 */
type LayoutRuleEntry =
  | number
  | string
  | {
      id?: number | string;
      value?: number | string;
      layout?: number | string;
      iconId?: string;
      icon?: string;
    };

type BuildOptionsInput = {
  amount?: number;
  layoutIcons?: ReadonlyArray<string | number>;
};

type ResolveOptionsInput = BuildOptionsInput & {
  channel?: string;
  type?: string;
  set?: string;
  product?: string;
};

const isPositiveInteger = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) > 0;

const toLayoutId = (value: unknown): number | null => {
  const numeric = Number(value);
  return isPositiveInteger(numeric) ? numeric : null;
};

const pickLayouts = (node: unknown): LayoutRuleEntry[] | null => {
  if (!node) return null;
  if (Array.isArray(node)) return node as LayoutRuleEntry[];
  const layouts = (node as {layouts?: unknown}).layouts;
  return Array.isArray(layouts) ? (layouts as LayoutRuleEntry[]) : null;
};

const normalizeRuleOption = (
  entry: LayoutRuleEntry,
  baseIconById: Map<number, string>
): LayoutOption | null => {
  if (entry === null || entry === undefined) return null;

  if (typeof entry === "number" || typeof entry === "string") {
    const id = toLayoutId(entry);
    if (!id) return null;
    return {
      id,
      iconId: String(baseIconById.get(id) ?? entry),
    };
  }

  if (typeof entry === "object") {
    const rawId = entry.id ?? entry.value ?? entry.layout;
    const id = toLayoutId(rawId);
    if (!id) return null;
    return {
      id,
      iconId: String(entry.iconId ?? entry.icon ?? baseIconById.get(id) ?? id),
    };
  }

  return null;
};

const dedupeById = (options: LayoutOption[]): LayoutOption[] => {
  const seen = new Set<number>();
  return options.filter((option) => {
    if (seen.has(option.id)) return false;
    seen.add(option.id);
    return true;
  });
};

export const buildLegacyLayoutOptions = ({
  amount,
  layoutIcons,
}: BuildOptionsInput): LayoutOption[] => {
  if (Array.isArray(layoutIcons) && layoutIcons.length > 0) {
    // Mixing numeric icons with non-numeric aliases risks id collisions —
    // e.g. ["1","2","3","hero","4"] would assign id=4 to both "hero"
    // (via index+1) and "4" (numeric). No current layoutIcons set mixes
    // them, so we don't guard against it; if that changes, dedupeById or
    // an explicit policy entry is the right fix.
    return layoutIcons.map((iconId, index) => {
      // When the icon string is a positive integer (e.g. "20"), treat it as
      // both the visual icon AND the canonical layout id. This is how sparse
      // icon sets like ["20", "21", ..., "27"] must be interpreted — the
      // previous "id = index + 1" behavior produced id=1 for icon "20",
      // writing mismatched values to the URL on click (issue #80).
      // Fall back to the index when the icon isn't a positive integer —
      // preserves behavior for any future non-numeric icon aliases.
      const numericId = toLayoutId(iconId);
      return {
        id: numericId ?? index + 1,
        iconId: String(iconId),
      };
    });
  }

  const count = toLayoutId(amount) ?? 0;
  return Array.from({length: count}, (_, index) => ({
    id: index + 1,
    iconId: String(index + 1),
  }));
};

const normalizeLayouts = (
  layouts: LayoutRuleEntry[],
  baseIconById: Map<number, string>
): LayoutOption[] =>
  dedupeById(
    layouts
      .map((entry) => normalizeRuleOption(entry, baseIconById))
      .filter((opt): opt is LayoutOption => opt !== null)
  );

export const resolveLayoutOptions = ({
  channel,
  type,
  set,
  product,
  amount,
  layoutIcons,
}: ResolveOptionsInput): LayoutOption[] => {
  const baseOptions = buildLegacyLayoutOptions({amount, layoutIcons});
  const baseIconById = new Map(baseOptions.map((option) => [option.id, option.iconId]));

  let resolved = baseOptions;

  const applyLayouts = (node: unknown) => {
    const layouts = pickLayouts(node);
    if (!layouts) return;
    const normalized = normalizeLayouts(layouts, baseIconById);
    if (normalized.length > 0) {
      resolved = normalized;
    }
  };

  // LAYOUT_POLICY is still JS-untyped; treat as `any` at the boundary.
  const policy = LAYOUT_POLICY as any;
  const channelNode = channel ? policy.channels?.[channel] : undefined;
  const typeNode = type ? channelNode?.types?.[type] : undefined;
  const setNode = set ? channelNode?.sets?.[set] : undefined;
  const productNode = product ? channelNode?.products?.[product] : undefined;
  const typeSetNode = set ? typeNode?.sets?.[set] : undefined;
  const typeProductNode = product ? typeNode?.products?.[product] : undefined;
  const setProductNode = product ? setNode?.products?.[product] : undefined;
  const typeSetProductNode = product ? typeSetNode?.products?.[product] : undefined;
  // Channel-independent product rules: a product whose own design fixes its
  // layouts states them once and keeps them in every channel that lists it.
  // A channel-scoped rule below is more specific and still wins.
  const rootProductNode = product ? policy.products?.[product] : undefined;
  const rootProductSetNode = set ? rootProductNode?.sets?.[set] : undefined;

  applyLayouts(policy.global);
  applyLayouts(channelNode);
  applyLayouts(typeNode);
  applyLayouts(setNode);
  applyLayouts(rootProductNode);
  applyLayouts(rootProductSetNode);
  applyLayouts(productNode);
  applyLayouts(typeSetNode);
  applyLayouts(typeProductNode);
  applyLayouts(setProductNode);
  applyLayouts(typeSetProductNode);

  // resolved is either baseOptions (same reference, possibly empty) or a
  // non-empty array from a policy override — applyLayouts guards both.
  return resolved;
};

export const getAllowedLayoutId = (
  candidate: unknown,
  layoutOptions: ReadonlyArray<LayoutOption>
): number => {
  const requested = toLayoutId(candidate);
  if (requested && layoutOptions.some((option) => option.id === requested)) {
    return requested;
  }
  return layoutOptions[0]?.id ?? 1;
};
